import { RequiredInfoValue, ServWithMods } from '@/util/types/serverTypes';

/** Property-level basis keys — not required info Mongo ObjectIds */
export const PROPERTY_BASIS_KEYS_FOR_REQUIRED_INFO = new Set([
  'sqft',
  'age',
  'unit count',
  'zipcode',
  'foundation',
  'requested',
]);

const PROPERTY_CONDITIONAL_ATTRIBUTES = new Set([
  'modifier-sqft',
  'modifier-age',
  'modifier-unit-count',
  'modifier-foundation',
  'modifier-zestimate',
]);

/** Modifiers whose inputs come from property (sqft, age, etc.) — used for workorder property-save pricing. */
export function serviceHasPropertyDrivenModifiers(service: ServWithMods): boolean {
  return (
    service.modifiers?.some((mod) => {
      if (mod.modType === 'conditional') {
        return (
          mod.modiferData?.conditions?.groups?.some((group) =>
            group.conditions.some((c) =>
              c.attribute ? PROPERTY_CONDITIONAL_ATTRIBUTES.has(c.attribute) : false
            )
          ) ?? false
        );
      }
      const basis = mod.modiferData?.basis;
      return (
        typeof basis === 'string' && PROPERTY_BASIS_KEYS_FOR_REQUIRED_INFO.has(basis)
      );
    }) ?? false
  );
}

/**
 * True if a service has modifiers driven by required-info (same notion as workorder
 * recalculation and the same `modifierPriceAdjFn` inputs used by
 * {@link calculateServicePrices} / {@link usePriceCalculation} in NewSchedulingForm).
 */
export function serviceHasRequiredInfoModifiers(service: ServWithMods): boolean {
  return (
    service.modifiers?.some((mod) => {
      if (mod.modType === 'conditional') {
        return (
          mod.modiferData?.conditions?.groups?.some((group) =>
            group.conditions.some((c) =>
              c.attribute?.startsWith('required-info-')
            )
          ) ?? false
        );
      }
      const basis = mod.modiferData?.basis;
      return (
        typeof basis === 'string' &&
        basis.length === 24 &&
        !PROPERTY_BASIS_KEYS_FOR_REQUIRED_INFO.has(basis)
      );
    }) ?? false
  );
}

/** Required-info field ids whose values differ between previous and next save payload. */
export function getChangedRequiredInfoFieldIds(
  previous: RequiredInfoValue[] | null | undefined,
  next: RequiredInfoValue[]
): Set<string> {
  const prevMap = new Map(
    (previous || []).map((v) => [v._requiredInfoId, v.value])
  );
  const changed = new Set<string>();
  for (const v of next) {
    const oldVal = prevMap.get(v._requiredInfoId);
    if (!prevMap.has(v._requiredInfoId) || String(oldVal) !== String(v.value)) {
      changed.add(v._requiredInfoId);
    }
  }
  for (const id of prevMap.keys()) {
    if (!next.some((v) => v._requiredInfoId === id)) {
      changed.add(id);
    }
  }
  return changed;
}

/**
 * True if any modifier on this service reads one of the given required-info ids
 * (ObjectId basis or `required-info-{id}` conditional attribute).
 */
export function serviceModifiersTouchRequiredInfoIds(
  service: ServWithMods,
  requiredInfoIds: Set<string>
): boolean {
  if (requiredInfoIds.size === 0) return false;
  return (
    service.modifiers?.some((mod) => {
      if (mod.modType === 'conditional') {
        return (
          mod.modiferData?.conditions?.groups?.some((group) =>
            group.conditions.some((c) => {
              const a = c.attribute;
              if (!a?.startsWith('required-info-')) return false;
              const rid = a.slice('required-info-'.length);
              return requiredInfoIds.has(rid);
            })
          ) ?? false
        );
      }
      const basis = mod.modiferData?.basis;
      if (
        typeof basis === 'string' &&
        basis.length === 24 &&
        !PROPERTY_BASIS_KEYS_FOR_REQUIRED_INFO.has(basis)
      ) {
        return requiredInfoIds.has(basis);
      }
      return false;
    }) ?? false
  );
}
